import type { Conversation, ConversationMessagesResponse } from '../types/messages';
import type { Activity } from '../types/activities';

/**
 * Extract participants from a message's details.
 * @param message - Activity message object
 * @returns Array of participant user IDs
 */
export function getParticipantsFromMessage(message: Activity): string[] {
  return (message.details?.participants as string[]) || [];
}

/**
 * Get recipients from a conversation, excluding the current user.
 * 
 * KISS: Prefer participants from conversation object directly instead of extracting from first message.
 * @param conversation - Conversation object or conversation messages response
 * @param currentUserId - Current user's ID
 * @returns Array of recipient user IDs (excluding current user)
 */
export function getRecipientsFromConversation(
  conversation: Conversation | ConversationMessagesResponse | null,
  currentUserId: string | undefined
): string[] {
  if (!conversation || !currentUserId) {
    return [];
  }

  // KISS: Prefer participants from conversation object directly (simplest approach)
  if ('participants' in conversation && Array.isArray(conversation.participants)) {
    return conversation.participants.filter((pid: string) => pid !== currentUserId);
  }

  // Fallback: Extract from first message if conversation object doesn't have participants
  if ('messages' in conversation && conversation.messages && conversation.messages.length > 0) {
    const firstMessage = conversation.messages[0];
    const participants = getParticipantsFromMessage(firstMessage);
    return participants.filter((pid: string) => pid !== currentUserId);
  }

  return [];
}

/**
 * Check if user has reacted to a specific reaction type on a message.
 * @param message - Activity message object
 * @param reaction - Reaction type to check
 * @param currentUserId - Current user's ID
 * @returns True if user has reacted, false otherwise
 */
export function hasUserReacted(
  message: Activity,
  reaction: 'thumbs_up' | 'thumbs_down' | 'question' | 'laugh',
  currentUserId: string | undefined
): boolean {
  if (!currentUserId || !message.metadata?.reactions) {
    return false;
  }

  const reactions = message.metadata.reactions as Record<string, string[]>;
  const reactionUsers = reactions[reaction];
  if (!reactionUsers) {
    return false;
  }

  return reactionUsers.includes(currentUserId);
}

/**
 * Toggle reaction for a message (add if not present, remove if present).
 * @param message - Activity message object
 * @param reaction - Reaction type to toggle
 * @param currentUserId - Current user's ID
 * @returns 'add' if reaction should be added, 'remove' if it should be removed
 */
export function getReactionToggleAction(
  message: Activity,
  reaction: 'thumbs_up' | 'thumbs_down' | 'question' | 'laugh',
  currentUserId: string | undefined
): 'add' | 'remove' {
  return hasUserReacted(message, reaction, currentUserId) ? 'remove' : 'add';
}

